/**
 * Deposit/withdraw chain configurations for the Nado collateral flows.
 *
 * Supports two bridging mechanisms:
 *   - USDT0/USDT: LayerZero OFT (legacy) or Across funding
 *   - USDC: Across funding / legacy CCTP + Stargate paths
 *
 * Addresses verified from:
 *   - https://docs.usdt0.to/technical-documentation/deployments
 *   - https://docs.layerzero.network/v2/deployments/chains/ink
 *   - On-chain verification via spotEngine.getToken(0) and getToken(5)
 *   - CCTP domain IDs verified via MessageTransmitterV2.localDomain()
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export type BridgeType = 'oft' | 'cctp' | 'across' | 'stargate' | 'none';

export type DepositTokenSymbol = 'USDT0' | 'USDC' | 'USDT';

/** Per-token config on a given chain */
export interface ChainTokenConfig {
  /** Display symbol */
  symbol: DepositTokenSymbol;
  /** ERC-20 token address on this chain */
  tokenAddress: `0x${string}`;
  /** Token decimals (6 for all stablecoins) */
  tokenDecimals: number;
  /** Nado spot product ID (0 = USDT0 quote, 5 = USDC) */
  productId: number;
  /** Bridge mechanism for cross-chain transfers */
  bridgeType: BridgeType;
  /** OFT contract address (only when bridgeType === 'oft') */
  oftAddress?: `0x${string}`;
  /** True for Ethereum USDT OFT Adapter (non-standard ERC-20 approve) */
  isAdapter?: boolean;
}

export interface DepositChainConfig {
  /** EVM chain ID */
  chainId: number;
  /** Display name */
  name: string;
  /** LayerZero v2 Endpoint ID (different from EVM chain ID) */
  lzEndpointId: number;
  /** The token address users hold and approve on this chain */
  tokenAddress: `0x${string}`;
  /** Display symbol for the user-facing token ("USDT" on Ethereum, "USDT0" elsewhere) */
  tokenSymbol: string;
  /** Token decimals (6 for all USDT0/USDT) */
  tokenDecimals: number;
  /** OFT or OFT Adapter contract address — call send()/quoteSend() here */
  oftAddress: `0x${string}`;
  /**
   * True for Ethereum where the OFT Adapter locks native USDT.
   * USDT has a non-standard ERC-20 (approve returns void).
   */
  isAdapter: boolean;
  /** Whether this is the primary (destination) chain */
  isPrimary: boolean;
  /** Native gas token symbol */
  nativeSymbol: string;
  /** Block explorer base URL */
  blockExplorerUrl: string;
  /** All tokens available on this chain (new — for multi-token support) */
  tokens: ChainTokenConfig[];
}

// ─── Constants ──────────────────────────────────────────────────────────────

/** LayerZero EndpointV2 address — identical on all EVM chains */
export const LZ_ENDPOINT_V2_ADDRESS =
  '0x1a44076050125825900e736c501f859c50fE728c' as const;

/** USDC token address on Ink (Circle native USDC, FiatTokenProxy) */
export const USDC_INK_ADDRESS =
  '0x2D270e6886d130D724215A266106e6832161EAEd' as const;

const isLocalDev = process.env.NODE_ENV === 'development';

// ─── Chain Configs ──────────────────────────────────────────────────────────

/**
 * Ink — primary (destination) chain.
 * Users deposit USDT0 or USDC to the Nado Endpoint contract here.
 */
export const INK_DEPOSIT_CHAIN: DepositChainConfig = {
  chainId: 57073,
  name: 'Ink',
  lzEndpointId: 30339,
  tokenAddress: '0x0200C29006150606B650577BBE7B6248F58470c1',
  tokenSymbol: 'USDT0',
  tokenDecimals: 6,
  oftAddress: '0x1cB6De532588fCA4a21B7209DE7C456AF8434A65',
  isAdapter: false,
  isPrimary: true,
  nativeSymbol: 'ETH',
  blockExplorerUrl: 'https://explorer.inkonchain.com',
  tokens: [
    {
      symbol: 'USDT0',
      tokenAddress: '0x0200C29006150606B650577BBE7B6248F58470c1',
      tokenDecimals: 6,
      productId: 0,
      bridgeType: 'none',
      oftAddress: '0x1cB6De532588fCA4a21B7209DE7C456AF8434A65',
    },
    {
      symbol: 'USDC',
      tokenAddress: USDC_INK_ADDRESS,
      tokenDecimals: 6,
      productId: 5,
      bridgeType: 'none',
    },
  ],
};

/**
 * Ethereum — source chain.
 * USDT: OFT Adapter that locks native USDT (non-standard ERC-20).
 * USDC: Circle CCTP v2 bridge.
 */
export const ETHEREUM_DEPOSIT_CHAIN: DepositChainConfig = {
  chainId: 1,
  name: 'Ethereum',
  lzEndpointId: 30101,
  tokenAddress: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
  tokenSymbol: 'USDT',
  tokenDecimals: 6,
  oftAddress: '0x6C96dE32CEa08842dcc4058c14d3aaAD7Fa41dee',
  isAdapter: true,
  isPrimary: false,
  nativeSymbol: 'ETH',
  blockExplorerUrl: 'https://etherscan.io',
  tokens: [
    {
      symbol: 'USDT',
      tokenAddress: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
      tokenDecimals: 6,
      productId: 0,
      bridgeType: 'oft',
      oftAddress: '0x6C96dE32CEa08842dcc4058c14d3aaAD7Fa41dee',
      isAdapter: true,
    },
    {
      symbol: 'USDC',
      tokenAddress: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
      tokenDecimals: 6,
      productId: 5,
      bridgeType: 'across',
    },
  ],
};

/**
 * Arbitrum One — source chain.
 * USDT0: Standard OFT with standard ERC-20.
 * USDC: Circle CCTP v2 bridge.
 */
export const ARBITRUM_DEPOSIT_CHAIN: DepositChainConfig = {
  chainId: 42161,
  name: 'Arbitrum One',
  lzEndpointId: 30110,
  tokenAddress: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9',
  tokenSymbol: 'USDT0',
  tokenDecimals: 6,
  oftAddress: '0x14E4A1B13bf7F943c8ff7C51fb60FA964A298D92',
  isAdapter: false,
  isPrimary: false,
  nativeSymbol: 'ETH',
  blockExplorerUrl: 'https://arbiscan.io',
  tokens: [
    {
      symbol: 'USDT0',
      tokenAddress: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9',
      tokenDecimals: 6,
      productId: 0,
      bridgeType: 'oft',
      oftAddress: '0x14E4A1B13bf7F943c8ff7C51fb60FA964A298D92',
    },
    {
      symbol: 'USDC',
      tokenAddress: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
      tokenDecimals: 6,
      productId: 5,
      bridgeType: 'across',
    },
  ],
};

/**
 * Base — source chain for bridge-first Across funding.
 */
export const BASE_DEPOSIT_CHAIN: DepositChainConfig = {
  chainId: 8453,
  name: 'Base',
  lzEndpointId: 30184,
  tokenAddress: '0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2',
  tokenSymbol: 'USDT',
  tokenDecimals: 6,
  oftAddress: '0x0000000000000000000000000000000000000000',
  isAdapter: false,
  isPrimary: false,
  nativeSymbol: 'ETH',
  blockExplorerUrl: 'https://basescan.org',
  tokens: [
    {
      symbol: 'USDT',
      tokenAddress: '0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2',
      tokenDecimals: 6,
      productId: 0,
      bridgeType: 'across',
    },
    {
      symbol: 'USDC',
      tokenAddress: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
      tokenDecimals: 6,
      productId: 5,
      bridgeType: 'across',
    },
  ],
};

// ─── Derived Lists ──────────────────────────────────────────────────────────

/** All supported deposit chains, primary first */
export const DEPOSIT_CHAINS: DepositChainConfig[] = [
  INK_DEPOSIT_CHAIN,
  ETHEREUM_DEPOSIT_CHAIN,
  ARBITRUM_DEPOSIT_CHAIN,
  BASE_DEPOSIT_CHAIN,
];

/**
 * Source chains only (excludes Ink primary).
 * All source chains are visible — token-level filtering happens via
 * getDepositChainsForToken() in the chain selector.
 */
export const SOURCE_CHAINS: DepositChainConfig[] = DEPOSIT_CHAINS.filter(
  (c) => !c.isPrimary,
);

/** Deposit chains visible in the UI */
export const DEPOSIT_DISPLAY_CHAINS: DepositChainConfig[] = DEPOSIT_CHAINS;

/** Withdrawal destination chains (all non-Ink chains, no filtering) */
export const WITHDRAW_DESTINATION_CHAINS: DepositChainConfig[] = DEPOSIT_CHAINS.filter(
  (c) => !c.isPrimary,
);

/** Lookup by EVM chain ID */
export const DEPOSIT_CHAIN_BY_ID: Record<number, DepositChainConfig> =
  Object.fromEntries(DEPOSIT_CHAINS.map((c) => [c.chainId, c]));

// ─── Token-aware helpers ────────────────────────────────────────────────────

/** Get the ChainTokenConfig for a specific token on a specific chain */
export function getChainToken(
  chainId: number,
  symbol: DepositTokenSymbol,
): ChainTokenConfig | undefined {
  const chain = DEPOSIT_CHAIN_BY_ID[chainId];
  return chain?.tokens.find((t) => t.symbol === symbol);
}

/** Get all chains that support a given token for deposits */
export function getDepositChainsForToken(
  symbol: DepositTokenSymbol,
): DepositChainConfig[] {
  return DEPOSIT_CHAINS.filter((chain) => {
    const token = chain.tokens.find((t) => t.symbol === symbol);
    if (!token) return false;
    // For USDT, hide Ethereum in production (non-standard approve quirks)
    if (token.symbol === 'USDT' && chain.isAdapter && !isLocalDev) return false;
    return true;
  });
}

/**
 * Get all chains that support a given token for withdrawals.
 *
 * For USDT0/USDT: excludes Ink (must bridge out via OFT — Ink is the source, not destination).
 * For USDC: includes Ink as a valid destination (bridgeType === 'none' → no bridge needed,
 * tokens stay on Ink in the user's wallet after the Nado withdrawal).
 */
export function getWithdrawChainsForToken(
  symbol: DepositTokenSymbol,
): DepositChainConfig[] {
  return DEPOSIT_CHAINS.filter((chain) => {
    const token = chain.tokens.find((t) => t.symbol === symbol);
    if (!token) return false;

    // For USDC, include Ink itself as a destination — bridgeType 'none' means
    // tokens land in the Ink wallet after withdrawal with no bridging required.
    if (symbol === 'USDC' && chain.isPrimary) return true;

    // For all other tokens (USDT0, USDT), exclude Ink — they must bridge out.
    if (chain.isPrimary) return false;

    return true;
  });
}

/** Get all unique token symbols available across all chains */
export function getAvailableTokenSymbols(): DepositTokenSymbol[] {
  const symbols = new Set<DepositTokenSymbol>();
  for (const chain of DEPOSIT_CHAINS) {
    for (const token of chain.tokens) {
      // Normalize: USDT deposits become USDT0 on Ink
      if (token.symbol === 'USDT') {
        symbols.add('USDT0');
      } else {
        symbols.add(token.symbol);
      }
    }
  }
  return Array.from(symbols);
}
